unit Sphinx.WebLogin;

{$I Sphinx.inc}

interface

uses
{$IFDEF PAS2JS}
  JS, Web, WebLib.Forms,
{$ENDIF}
  System.Classes, System.SysUtils,
  Sphinx.LoginCommon,
  Sphinx.OidcClient;

const
{$IF COMPILERVERSION > 28}
  pidWeb = $10000;
{$ELSE}
  pidWeb = $1000;
{$IFEND}
{$IFDEF PAS2JS}
  TMSWebPlatform = pidWeb;
{$ELSE}
  TMSWebPlatform = pidWin32 + pidWin64 + pidWeb;
{$ENDIF}

type
  TUserLoggedInArgs = Sphinx.LoginCommon.TUserLoggedInArgs;
  TAuthResult = Sphinx.OidcClient.TAuthResult;

  TValidateIssuerEvent = procedure(Sender: TObject; const Issuer,
    ExpectedIssuer: string; var Valid: Boolean) of object;

  [ComponentPlatforms(TMSWebPlatform)]
  /// <summary>
  ///   Component to authenticate a TMS Web Core application to a Sphinx server.
  /// </summary>
  /// <remarks>
  ///   Use TSphinxWebLogin component to perform authentication and authorization to a Sphinx server from a TMS Web Core application.
  ///
  ///   It abstracts the OAuth 2 authentication code with PKCE flow required by Sphinx, by discovering the authorization and token
  ///   endpoints, performing the HTTP requests passing the proper parameters, processing the response provided to the
  ///   redirect URLs and saving the identity and access tokens in to local web storage.
  ///
  ///   Usage is simple, just properly set some key properties like <see cref="Authority">Authority</see>,
  ///   <see cref="ClientId">ClientId</see> and <see cref="Scope">Scope</see> and then use available methods like
  ///   <see cref="Login">Login</see> to effectively login to the server.
  /// </remarks>
  TSphinxWebLogin = class(TComponent)
  private
    FStorage: TLoginStorage;
    FClient: TOidcClient;
    FScope: string;
    FRedirectUri: string;
    FOnUserLoggedIn: TUserLoggedInEvent;
    FOnValidateIssuer: TValidateIssuerEvent;
    function GetStorage: TLoginStorage;
    procedure SetAuthority(const Value: string);
    procedure SetClientId(const Value: string);
    procedure ReleaseStorage;
    function GetAuthority: string;
    function GetClientId: string;
    function CheckCallback: Boolean; {$IFDEF PAS2JS}async;{$ENDIF}
    procedure SetupOidcClient;
    function GetSkipIssuerValidation: Boolean;
    procedure SetSkipIssuerValidation(const Value: Boolean);
    procedure DoValidateIssuer(const Issuer, ExpectedIssuer: string; var Valid: Boolean);
    function CanRefresh: Boolean;
  strict protected
    procedure DoUserLoggedIn(LoginResult: TAuthResult);
    property Storage: TLoginStorage read GetStorage;
    property Client: TOidcClient read FClient;
  protected
    procedure Loaded; override;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;

    /// <summary>
    ///   Starts the process to login to the web application.
    /// </summary>
    /// <remarks>
    ///   The method follows this sequence:
    ///
    ///   1. If the current page URL contains a valid authorization callback (state parameter
    ///   matching a pending request), the authorization code is exchanged for tokens, the tokens
    ///   are saved in browser local storage, and <see cref="OnUserLoggedIn">OnUserLoggedIn</see>
    ///   is fired. No further steps are taken.
    ///
    ///   2. If the user is already logged in (valid, non-expired tokens in local storage),
    ///   <see cref="OnUserLoggedIn">OnUserLoggedIn</see> is fired immediately with the cached
    ///   result.
    ///
    ///   3. If the stored tokens are expired but a refresh token is available, a silent token
    ///   refresh is attempted. If the refresh succeeds, <see cref="OnUserLoggedIn">OnUserLoggedIn</see>
    ///   is fired with the new tokens. If the refresh fails, the browser is redirected to the
    ///   Sphinx login page (step 4).
    ///
    ///   4. The browser is redirected to the Sphinx login page. After successful authentication,
    ///   Sphinx redirects back to the URL in <see cref="RedirectUri">RedirectUri</see>. The web
    ///   application reloads and step 1 processes the authorization code.
    ///
    ///   To obtain refresh tokens so that silent refresh works on subsequent calls, include the
    ///   <c>offline_access</c> scope in the <see cref="Scope">Scope</see> property.
    /// </remarks>
    procedure Login; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    ///   Logs out the user from the web application.
    /// </summary>
    /// <remarks>
    ///   This method removes the tokens saved in browser local storage. This clears up information about the logged user,
    ///   so it's not available anymore. A call to <see cref="Login">Login</see> method will again redirect to the login page.
    ///   Note that this only clears the local session. The SSO session at the identity provider remains active.
    ///   Use <see cref="LogoutAndEndSession">LogoutAndEndSession</see> to also terminate the SSO session.
    /// </remarks>
    procedure Logout;

    /// <summary>
    ///   Logs out the user and terminates the SSO session at the identity provider.
    /// </summary>
    /// <remarks>
    ///   In addition to clearing the local tokens (like <see cref="Logout">Logout</see>), this method
    ///   redirects the browser to the identity provider's end-session endpoint, which terminates
    ///   the SSO session server-side. After this, other applications sharing the same SSO session
    ///   will also require the user to log in again.
    ///
    ///   If the identity provider does not advertise an end-session endpoint (e.g. the discovery
    ///   document has no <c>end_session_endpoint</c>), the method behaves exactly like
    ///   <see cref="Logout">Logout</see> and no redirect occurs.
    /// </remarks>
    procedure LogoutAndEndSession; {$IFDEF PAS2JS}async;{$ENDIF}

    /// <summary>
    ///   Indicates if a user is logged in the application.
    /// </summary>
    function IsLoggedIn: Boolean;

    /// <summary>
    ///   Provides information about the logged user.
    /// </summary>
    /// <remarks>
    ///   If there is a user logged in the application, this property provides detailed information about him/her, including
    ///   the identity and access tokens. If no user is logged in, nil is returned.
    /// </remarks>
    function AuthResult: TAuthResult;

    /// <summary>
    ///   Refreshes the stored tokens using the stored refresh token.
    /// </summary>
    /// <param name="AScope">
    ///   Optional scope to request. If empty, the server grants the same scope as the original
    ///   authorization. The requested scope cannot exceed the scope of the original grant.
    /// </param>
    /// <returns>
    ///   The new <c>TAuthResult</c> with updated tokens on success, or <c>nil</c> if the refresh
    ///   could not be completed (no stored result, no refresh token, or the server rejected the
    ///   request).
    /// </returns>
    /// <remarks>
    ///   On success, the new tokens are automatically saved to browser local storage, so
    ///   subsequent calls to <see cref="AuthResult">AuthResult</see> and
    ///   <see cref="IsLoggedIn">IsLoggedIn</see> reflect the refreshed state. The method never
    ///   raises an exception; all failures are reported by returning <c>nil</c>.
    ///
    ///   Refresh tokens are only issued by the server when the <c>offline_access</c> scope was
    ///   included in the original authorization request via the <see cref="Scope">Scope</see>
    ///   property. If the server implements token rotation, the new refresh token replaces the old
    ///   one in storage automatically.
    /// </remarks>
    function RefreshTokens(const AScope: string = ''): TAuthResult; {$IFDEF PAS2JS}async;{$ENDIF}
  published
    /// <summary>
    ///   The base URL of the Sphinx server to authenticate to.
    /// </summary>
    /// <remarks>
    ///   This property is required and must contain the base URL of the Sphinx server used to authenticate the users.
    ///   From this URL, the component will automatically find the authorization endpoint (usually at /oauth/authorize)
    ///   and the token endpoint (usually at /oauth/token) to perform the correct requests.
    ///
    ///   Thus, you should not provide the URL to the endpoints themselves, but to the root URL of the Sphinx server.
    /// </remarks>
    property Authority: string read GetAuthority write SetAuthority;

    /// <summary>
    ///   The client id used to identity the web application in the Sphinx server.
    /// </summary>
    /// <remarks>
    ///   A required property that should contain the client id of web application. This should match the id of one of the
    ///   client applications registered in the Sphinx server. The registered client application should support
    ///   the authorization code grant type, and it's also recommended that it requires PKCE for increased security.
    ///   It should also have the web application URL registered in the list of valid redirect uris.
    /// </remarks>
    property ClientId: string read GetClientId write SetClientId;

    /// <summary>
    ///   The scope to be required to the Sphinx server.
    /// </summary>
    /// <remarks>
    ///   This should contian the scope being requested to the server. At the very least this should include "openid"
    ///   but you can also add "email". If your web application communicates with a REST API, then you can also include
    ///   the scopes that your API might require.
    /// </remarks>
    property Scope: string read FScope write FScope;

    /// <summary>
    ///   The redirect URI used to process the request sent by the server with the authorization code.
    /// </summary>
    /// <remarks>
    ///   This property should just contain the URL where your web application is available. When the server requests this URL,
    ///   the web application will be reloaded, the component will be created, and automatically process the parameters
    ///   sent to the redirect URL, if any
    /// </remarks>
    property RedirectUri: string read FRedirectUri write FRedirectUri;

    /// <summary>
    ///   Event fired whenever a user is logged in.
    /// </summary>
    property OnUserLoggedIn: TUserLoggedInEvent read FOnUserLoggedIn write FOnUserLoggedIn;

    /// <summary>
    ///   Gets or sets whether issuer validation is skipped during ID token processing.
    /// </summary>
    /// <remarks>
    ///   When <c>False</c> (the default), the <c>iss</c> claim in the identity token must exactly
    ///   match the configured <see cref="Authority">Authority</see>. Set to <c>True</c> when
    ///   connecting to multi-tenant identity providers -- for example, Microsoft Entra using the
    ///   <c>organizations</c> authority -- where the per-tenant issuer in each token never matches
    ///   the authority URL. Use <see cref="OnValidateIssuer">OnValidateIssuer</see> to supply
    ///   custom validation logic in place of the standard string comparison. Defaults to <c>False</c>.
    /// </remarks>
    property SkipIssuerValidation: Boolean read GetSkipIssuerValidation
      write SetSkipIssuerValidation default False;

    /// <summary>
    ///   Event fired to allow custom validation of the issuer claim in the identity token.
    /// </summary>
    /// <remarks>
    ///   This event fires on every ID token validation regardless of the value of
    ///   <see cref="SkipIssuerValidation">SkipIssuerValidation</see>. The <c>Valid</c> parameter
    ///   is pre-initialized to the result of the standard issuer comparison (or to <c>True</c> when
    ///   <c>SkipIssuerValidation</c> is <c>True</c>), so the handler can either accept the default
    ///   decision or override it. Set <c>Valid</c> to <c>False</c> or raise an exception to reject
    ///   the token. The handler receives the actual issuer from the token and the expected issuer
    ///   derived from <see cref="Authority">Authority</see>.
    /// </remarks>
    property OnValidateIssuer: TValidateIssuerEvent read FOnValidateIssuer write FOnValidateIssuer;
  end;

implementation

{ TSphinxWebLogin }

function TSphinxWebLogin.AuthResult: TAuthResult;
begin
  Result := FStorage.AuthResult;
end;

function TSphinxWebLogin.CheckCallback: Boolean;
var
  CallbackUrl: string;
  AuthResult: TAuthResult;
begin
  SetupOidcClient;
{$IFDEF PAS2JS}
  CallbackUrl := window.location.href;
{$ELSE}
  CallbackUrl := '';
{$ENDIF}
  Result := Client.IsValidCallback(CallbackUrl);
  if Result then
  begin
    AuthResult := {$IFDEF PAS2JS}await{$ENDIF}(Client.FinishAuthorize(CallbackUrl));
    Storage.Save(AuthResult);
    DoUserLoggedIn(AuthResult);
  end
end;

function TSphinxWebLogin.CanRefresh: Boolean;
var
  AuthResult: TAuthResult;
begin
  AuthResult := Storage.AuthResult;
  Result := (AuthResult <> nil) and (AuthResult.RefreshToken <> '');
end;

function TSphinxWebLogin.GetSkipIssuerValidation: Boolean;
begin
  Result := FClient.SkipIssuerValidation;
end;

procedure TSphinxWebLogin.SetSkipIssuerValidation(const Value: Boolean);
begin
  FClient.SkipIssuerValidation := Value;
end;

procedure TSphinxWebLogin.DoValidateIssuer(const Issuer, ExpectedIssuer: string; var Valid: Boolean);
begin
  if Assigned(FOnValidateIssuer) then
    FOnValidateIssuer(Self, Issuer, ExpectedIssuer, Valid);
end;

function TSphinxWebLogin.RefreshTokens(const AScope: string = ''): TAuthResult;
var
  CurrentResult: TAuthResult;
begin
  Result := nil;
  CurrentResult := Storage.AuthResult;
  if (CurrentResult = nil) or (CurrentResult.RefreshToken = '') then
    Exit;
  try
    Result := {$IFDEF PAS2JS}await{$ENDIF}(Client.RefreshTokens(CurrentResult.RefreshToken, AScope));
    Storage.Save(Result);
  except
    Result := nil;
  end;
end;

constructor TSphinxWebLogin.Create(AOwner: TComponent);
begin
  inherited;
  FClient := TOidcClient.Create;
  FClient.OnValidateIssuer := DoValidateIssuer;
end;

destructor TSphinxWebLogin.Destroy;
begin
  FStorage.Free;
  FClient.Free;
  inherited;
end;

procedure TSphinxWebLogin.DoUserLoggedIn(LoginResult: TAuthResult);
var
  Args: TUserLoggedInArgs;
begin
  if not Assigned(FOnUserLoggedIn) then Exit;

  Args := TUserLoggedInArgs.Create(Self, LoginResult);
  try
    FOnUserLoggedIn(Args);
  finally
    Args.Free;
  end;
end;

function TSphinxWebLogin.GetAuthority: string;
begin
  Result := Client.Authority;
end;

function TSphinxWebLogin.GetClientId: string;
begin
  Result := Client.ClientId;
end;

function TSphinxWebLogin.GetStorage: TLoginStorage;
begin
  if FStorage = nil then
    FStorage := TLoginStorage.Create(Authority, ClientId);
  Result := FStorage;
end;

function TSphinxWebLogin.IsLoggedIn: Boolean;
var
  AuthResult: TAuthResult;
begin
  AuthResult := Storage.AuthResult;
  Result := (AuthResult <> nil) and (AuthResult.IdToken <> '');
  if Result then
    if (AuthResult.AccessToken <> '') and AuthResult.IsExpired then
      Result := False;
end;

procedure TSphinxWebLogin.Loaded;
begin
  inherited;
  CheckCallback;
end;

procedure TSphinxWebLogin.Login;
{$IFDEF PAS2JS}
var
  Refreshed: TAuthResult;
{$ENDIF}
begin
  SetupOidcClient;
  if not {$IFDEF PAS2JS}await{$ENDIF}(CheckCallback) then
  begin
    if IsLoggedIn then
      DoUserLoggedIn(Storage.AuthResult)
    else if CanRefresh then
    begin
{$IFDEF PAS2JS}
      Refreshed := await(RefreshTokens);
      if Refreshed <> nil then
        DoUserLoggedIn(Storage.AuthResult)
      else
        window.location.href := await(Client.StartAuthorize(window.location.hash)).AuthorizeUrl;
{$ENDIF}
    end
{$IFDEF PAS2JS}
    else
      window.location.href := await(Client.StartAuthorize(window.location.hash)).AuthorizeUrl;
{$ENDIF}
  end;
end;

procedure TSphinxWebLogin.Logout;
begin
  if FStorage <> nil then
    FStorage.Clear;
end;

procedure TSphinxWebLogin.LogoutAndEndSession;
var
  IdToken: string;
  EndSessionUrl: string;
begin
  IdToken := '';
  if FStorage <> nil then
  begin
    if Storage.AuthResult <> nil then
      IdToken := Storage.AuthResult.IdToken;
    FStorage.Clear;
  end;

  SetupOidcClient;
  EndSessionUrl := {$IFDEF PAS2JS}await{$ENDIF}(Client.BuildLogoutUrl(IdToken));
  if EndSessionUrl <> '' then
{$IFDEF PAS2JS}
    window.location.href := EndSessionUrl;
{$ENDIF}
end;

procedure TSphinxWebLogin.ReleaseStorage;
begin
  FreeAndNil(FStorage);
end;

procedure TSphinxWebLogin.SetAuthority(const Value: string);
begin
  if Client.Authority <> Value then
  begin
    Client.Authority := Value;
    ReleaseStorage;
  end;
end;

procedure TSphinxWebLogin.SetClientId(const Value: string);
begin
  if Client.ClientId <> Value then
  begin
    Client.ClientId := Value;
    ReleaseStorage;
  end;
end;

procedure TSphinxWebLogin.SetupOidcClient;
begin
  Client.Scope := Scope;
  Client.RedirectUri := RedirectUri;
end;


initialization
{$IFDEF PAS2JS}
  Application.HandleOAuth := False;
{$ENDIF}

end.
